import { revealCells, type BoardGraph } from '../board/graph';
import { parseHexId, type HexId } from '../board/axial';
import type { GameRuntime, GameState, TerrainOverlay } from './state';

// The LIVE board (H20-S1, plan §4.3): `env.board` plus whatever play has decided since.
//
// A fog cell's terrain is decided mid-game, but `BoardGraph` is derived once from the public map
// and never lives in state. So modules contribute overlays (`ModuleCtx.terrainOverlay`) and every
// rule that reads terrain asks THIS instead of `env.board`. With no overlay registered — every
// classic game — it returns `env.board` ITSELF (reference identity, pinned by T-SEA-001): the
// overlay machinery literally does not run.
//
// The trap it closes is the D38 class: a static read of a revealed hex is silently wrong in
// exactly one mode and only after a discovery. Nothing fails; the hex just never pays.

/** Canonical tuple order over hex ids (q, then r) — the one order a reveal is applied in. */
function compareHexIds(a: HexId, b: HexId): number {
  const pa = parseHexId(a);
  const pb = parseHexId(b);
  return pa.q - pb.q || pa.r - pb.r;
}

export function liveBoard(state: GameState, env: GameRuntime): BoardGraph {
  const overlays = env.composed.terrainOverlays;
  if (overlays.length === 0) return env.board;
  let merged: TerrainOverlay | null = null;
  for (const { overlay } of overlays) {
    const o = overlay(state);
    for (const id of Object.keys(o)) (merged ??= {})[id] = o[id]!;
  }
  if (merged === null) return env.board;
  const cells = merged;
  // SORTED, never insertion order: the overlay's Record round-trips through canonical JSON on
  // every replay and comes back key-sorted (D25) — iterating it as inserted would build a
  // different Map live than on replay, and bots and the §13.4 policy tie-break on Map order
  const ids = Object.keys(cells).sort(compareHexIds);
  const key = ids.map((id) => {
    const c = cells[id]!;
    return c === 'water' ? `${id}=~` : `${id}=${c.tile}/${c.disc ?? '-'}`;
  }).join(';');
  const memo = env.liveMemo !== undefined && env.liveMemo.board === env.board ? env.liveMemo : null;
  const hit = memo?.byKey.get(key);
  if (hit) return hit;
  const graph = revealCells(env.board, ids.map((id) => [id, cells[id]!] as const));
  memo?.byKey.set(key, graph);
  return graph;
}
